import { Trans } from '@lingui/react/macro'
import { Chatbot } from 'features/chatbot/Chatbot'
import { useChatbotDockHost } from 'features/chatbot/ChatbotDock'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { PERMISSION_CANTILEVER_CHAT_USER } from 'types'

const PANEL_WIDTH = '25rem'
const PANEL_GAP = '0.75rem'

/**
 * Inline right-hand panel for legacy layouts. Collapses via negative
 * margin-right so surrounding flex layouts don't reflow when hidden.
 */
const LegacyPanelContainer = styled.div<{ $isOpen: boolean }>`
  width: ${PANEL_WIDTH};
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  padding-right: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.background2_5};
  border-left: 1px solid ${({ theme }) => theme.forecast.border};
  margin-right: ${({ $isOpen }) => ($isOpen ? '0' : `-${PANEL_WIDTH}`)};
  transition: margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &[aria-hidden='true'] {
    pointer-events: none;
  }
`

/**
 * Floating agent panel for the embedded-sidebar shell; portaled into the
 * workspace dock beside the main content card. Inner body stays full width
 * so content does not reflow during open/close.
 */
const DockedPanelFrame = styled.aside<{ $isOpen: boolean }>`
  --chatbot-panel-width: ${PANEL_WIDTH};
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: ${({ $isOpen }) => ($isOpen ? 'var(--chatbot-panel-width)' : '0')};
  margin-left: ${({ $isOpen }) => ($isOpen ? PANEL_GAP : '0')};
  transition:
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &[aria-hidden='true'] {
    pointer-events: none;
  }
`

const DockedPanelBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: var(--chatbot-panel-width);
  height: 100%;
  min-height: 0;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: var(--radius, 0.5rem);
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  overflow: hidden;
`

const NoAccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface ChatbotSidePanelProps {
  isOpen: boolean
  onClose: () => void
  useExistingProvider?: boolean
  legacyAutoScroll?: boolean
}

function ChatbotPanelContent({
  onClose,
  useExistingProvider,
  legacyAutoScroll,
}: {
  onClose: () => void
  useExistingProvider: boolean
  legacyAutoScroll: boolean
}) {
  return (
    <PermissionsGate
      requires={[PERMISSION_CANTILEVER_CHAT_USER]}
      fallback={
        <NoAccessContainer>
          <strong>
            <Trans>AI Chat unavailable</Trans>
          </strong>
          <span>
            <Trans>
              You don&apos;t have access to this feature. Contact your admin to
              request the <code>cantilever:chat:user</code> permission.
            </Trans>
          </span>
        </NoAccessContainer>
      }>
      <Chatbot
        useV2
        onClose={onClose}
        useExistingProvider={useExistingProvider}
        legacyAutoScroll={legacyAutoScroll}
      />
    </PermissionsGate>
  )
}

export const ChatbotSidePanel: React.FC<ChatbotSidePanelProps> = ({
  isOpen,
  onClose,
  useExistingProvider = false,
  legacyAutoScroll = false,
}) => {
  const chatbotDockHost = useChatbotDockHost()
  const isDocked = chatbotDockHost !== null

  if (isDocked) {
    const panel = (
      <DockedPanelFrame
        $isOpen={isOpen}
        aria-hidden={!isOpen}
        data-testid='chatbot-side-panel'>
        <DockedPanelBody>
          <ChatbotPanelContent
            onClose={onClose}
            useExistingProvider={useExistingProvider}
            legacyAutoScroll={legacyAutoScroll}
          />
        </DockedPanelBody>
      </DockedPanelFrame>
    )

    return createPortal(panel, chatbotDockHost)
  }

  return (
    <LegacyPanelContainer
      $isOpen={isOpen}
      aria-hidden={!isOpen}
      data-testid='chatbot-side-panel'>
      <ChatbotPanelContent
        onClose={onClose}
        useExistingProvider={useExistingProvider}
        legacyAutoScroll={legacyAutoScroll}
      />
    </LegacyPanelContainer>
  )
}
